import React, { useState, useEffect, useCallback } from "react";
import { setTitle } from "../helpers/MetaTag";
import ButtonFile from "../components/form/ButtonFile";
import Table from "../components/elements/table/Table";
import Button from "../components/form/Button";
import Select from "../components/form/Select";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { reorder } from "../helpers";
import toastr from "toastr";
import moment from "moment";
import NoDataFound from "../components/NoDataFound";
import Searching from "../components/Searching";
import { useSelector } from "react-redux";

import { postData } from "../services/api";
import { useTranslation } from "react-i18next";
import { CSVLink, CSVDownload } from "react-csv";
import Papa from "papaparse";
import ManageEmployeePopup from "../components/popup/ManageEmployeePopup";
import DeletePopup from "../components/DeletePopup";
import ReactPaginate from "react-paginate";
import TableSkeleton from "../components/loader/TableSkeleton";
import { Tooltip } from "react-tooltip";
import EmployeeUploadPopup from "../components/popup/EmployeeUploadPopup";
import ExternalEmployeePopup from "../components/popup/ExternalEmployeePopup";
import Pagination from "../components/Common/Pagination";
import TableSort from "../components/elements/table/TableSort";

const CompanyEmployees = () => {
  setTitle("Succession Planning Software for your Business with Succession Now | Company Employees");
  const [loading, setLoading] = useState(true);
  const { i18n, t } = useTranslation();
  const role = localStorage.getItem("role");
  const user = useSelector((state) => state.auth?.user);
  const time = new Date().getTime();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [noOfPage, setNoOfPage] = useState(0);
  const [position, setPosition] = useState([]);
  const [sortKey, setSortKey] = useState("created_at");
  const [sortValue, setSortValue] = useState(-1);
  const [sortQuery, setSortQuery] = useState("");
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [startDataCount, setStartDataCount] = useState(0);
  const [endDataCount, setEndDataCount] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [isUpdateFile, setIsUpdateFile] = useState(false);
  const [actionValue, setActionValue] = useState({});
  const [enabledManageEmployeePopup, setEnabledManageEmployeePopup] =
    useState(false);
  const [details, setDetails] = useState({});
  const [currentId, setCurrentId] = useState("");
  const [enabledDeletePopup, setenabledDeletePopup] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [enabledUploadEmployeePopup, setEnabledUploadEmployeePopup] =
    useState(false);
  const [enabledExternalEmployeePopup,setEnabledExternalEmployeePopup] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    if (sortKey && sortValue) {
      setSortQuery({ [sortKey]: sortValue });
    } else {
      setSortQuery("");
    }
  }, [sortKey, sortValue]);

  const loadPositionList = useCallback(async () => {
    try {
      const result = await postData("position/list", {
        sortQuery: { created_at: 1 },
        status: 1,
        isBoardMember: 2,
      });
      if (result.data) {
        setPosition(
          result.data?.sort((a, b) => a?.name?.localeCompare(b?.name))
        );
      } else {
        console.log("Position list message", result.message);
      }
    } catch (error) {
      console.log("Error position list catch", error.message);
    }
  }, [actionValue || '']);

  useEffect(() => {
    loadPositionList();
  }, [loadPositionList]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await postData("employee/list", {
        limit: limit,
        offset: offset,
        sortQuery: sortQuery,
      });
      if (result.data) {
        setCsvData(result.data);
      }
      setTotalDataCount(result.count);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err.message);
    }
  }, [limit, offset, sortQuery, isUpdateFile, actionValue]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    setNoOfPage(Math.ceil(totalDataCount / limit));
    const startItem = Number(totalDataCount > 0 ? 1 : 0);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  }, [limit, totalDataCount]);

  const handelUpdate = async (id, val, field, index) => {
    try {
      setCsvData(
        csvData.map((it, idx) => {
          if (idx === index) {
            return { ...it, [field]: val };
          } else {
            return it;
          }
        })
      );
      const result = await postData("employee/update", {
        id: id,
        [field]: val !== "" ? val : null,
        type: "position",
      });
      if (result.status && result.status === 200) {
        toastr.success(result.message);
      } else {
        toastr.error(result.message);
      }
    } catch (error) {
      toastr.error(error.message);
    }
  };

  const handlePageClick = (event) => {
    const newOffset = (event.selected * limit) % totalDataCount;
    setCurrentPage(event.selected);
    setOffset(newOffset);
    const startItem = Number(newOffset + 1);
    const endItem = Math.min(startItem + limit - 1, totalDataCount);
    setStartDataCount(startItem);
    setEndDataCount(endItem);
  };

  useEffect(() => {
    if (actionValue && actionValue.button_type === "delete") {
      const loadDeleteRecord = async () => {
        setIsUpdate(false);
        try {
          const statusData = await postData("employee/delete", {
            id: actionValue?.row_id,
          });
          if (statusData.data) {
            const index = csvData.findIndex(
              (item) => item._id === actionValue?.row_id
            );
            if (index !== -1) {
              csvData.splice(index, 1);
              setCsvData(
                csvData.map((it) => {
                  return it;
                })
              );
            }
            setIsUpdate(true);
          } else {
            toastr.error(statusData.message);
          }
        } catch (error) {
          toastr.error(error.message);
        }
      };
      loadDeleteRecord();
    }
  }, [actionValue]);

  return (
    <>
      <div className="relative flex justify-between items-center mb-4 flex-wrap sm:flex-nowrap gap-y-4">
        <div className="text-xl md:text-3xl text-slate-800 font-bold">
          {t("company_employees") + " - " + user?.company_name}
        </div>

        <div className="sm:ml-auto flex justify-start sm:justify-end items-center gap-2 flex-wrap">
          <div
            className="sm:ml-auto flex justify-end items-center"
            data-tooltip-id={`buttons${1}`}
            data-tooltip-place="bottom"
          >
            <Button
              buttonLabelClasses=""
              buttonType={"button"}
              buttonIcon={"fa-light fa-arrow-right-to-arc"}
              buttonIconPosition={"left"}
              buttonLabel={t("add_employee")}
              buttonHasLink={false}
              functions={() => setEnabledManageEmployeePopup(true)}
              buttonClasses = {
                "!rounded-full !bg-transparent !text-black !border !border-black"
              }
            />

            <Tooltip
              id={`buttons${1}`}
              className="!text-xs !bg-dark-teal-600 z-50 rounded py-3 px-6"
              render={() => (
                <div className="gap-x-1">
                  <div className="text-[13px] font-normal leading-none flex items-center">
                    <div className="font-semibold">Add New Employee</div>
                  </div>
                </div>
              )}
            />
          </div>
          <Button
            buttonLabelClasses=""
            buttonType={"button"}
            buttonIcon={"fa-light fa-upload"}
            buttonIconPosition={"left"}
            buttonLabel={t("upload_employee")}
            buttonHasLink={false}
            functions={() => setEnabledUploadEmployeePopup(true)}
            buttonClasses = {
              "!rounded-full !bg-teal-500 !text-white !border !border-teal"
            }
          />
          {
            (user?.email === "jd.test.nwa@gmail.com" ||  user?.email === "sujaymondal9@gmail.com") && 
            <Button
              buttonLabelClasses=""
              buttonType={"button"}
              buttonIcon={"fa-light fa-users"}y
              buttonIconPosition={"left"}
              buttonLabel={'Performance Pro Employee'}
              buttonHasLink={false}
              functions={() => setEnabledExternalEmployeePopup(true)}
              buttonClasses = {
                "!rounded-full !bg-slate-700 !text-white !border !border-black"
              }
            />   
          }             
        </div>
      </div>
      <div className="relative flex justify-between items-center mb-4"></div>
      <div className="relative flex gap-3 items-center mb-3 flex-wrap">
        <div className="relative">
          <Select
            xPlacement={"left"}
            dropdownButtonClass={"!bg-white"}
            dropdownClass={"w-auto"}
            selectedValue={limit}
            dropdownData={[
              { _id: 1000000, label: t("all_items"), value: 1000000 },
              ...[
                { _id: 10, label: "10 " + t("items"), value: 10 },
                { _id: 20, label: "20 " + t("items"), value: 20 },
                { _id: 30, label: "30 " + t("items"), value: 30 },
                { _id: 50, label: "50 " + t("items"), value: 50 },
                { _id: 100, label: "100 " + t("items"), value: 100 },
              ],
            ]}
            getSelectedValue={(e) => {
              setLimit(e.value);
              setOffset(0);
               setCurrentPage(0);
            }}
          />
        </div>
      </div>
      <div className="w-full overflow-auto scroll-smooth scrollbar min-h-[480px] mb-4">
        <table className="min-w-max w-full table-auto">
          <thead>
            <tr className="text-white font-bold text-sm leading-none border-b border-slate-200">
              <th className="py-4 px-2 text-left max-w-[240px] align-top sticky -top-6 z-10 bg-slate-900 rounded-tl-md">
                <div className={"flex items-center w-full justify-end"}>
                  <div className="text-sm font-semibold text-white capitalize">
                    {t("first_name")}
                  </div>                  
                  <div className="ml-auto flex justify-end items-center">
                     <div
                        className="relative flex items-center"
                        onClick={() => {
                          setSortKey('first_name');
                        }}
                      >
                        <TableSort
                          sortState={"nostate"}
                          getSortValue={setSortValue}
                        />
                      </div>                      
                  </div>
                </div>                
              </th>
              <th className="py-4 px-2 text-left max-w-[240px] align-top sticky -top-6 z-10 bg-slate-900">
                <div className={"flex items-center w-full justify-end"}>
                  <div className="text-sm font-semibold text-white capitalize">
                    {t("last_name")}
                  </div>                  
                  <div className="ml-auto flex justify-end items-center">
                     <div
                        className="relative flex items-center"
                        onClick={() => {
                          setSortKey('last_name');
                        }}
                      >
                        <TableSort
                          sortState={"nostate"}
                          getSortValue={setSortValue}
                        />
                      </div>                      
                  </div>
                </div>                
              </th>
              <th className="py-4 px-2 text-left max-w-[240px] align-top sticky -top-6 z-10 bg-slate-900">
                {t("email")}
              </th>
              <th className="py-4 px-2 text-left max-w-[240px] align-top sticky -top-6 z-10 bg-slate-900">
                {t("position")}
              </th>
              <th className="py-4 px-2 text-left max-w-[240px] align-top sticky -top-6 z-10 bg-slate-900">
                {t("date")}
              </th>
              <th className="py-4 px-2 text-left max-w-[240px] align-top sticky -top-6 z-10 bg-slate-900">
                {t("action")}
              </th>
            </tr>
          </thead>
          {totalDataCount > 0 ? (
            <tbody className="text-slate-700 text-sm font-light bg-white">
              {csvData.map((items, index) => {
                let firstName = "";
                let lastName = "";

                if (!items?.first_name || items?.first_name.toLowerCase() === "null") {
                  const parts = (items?.name || "").split(/\s+/).filter(Boolean);

                  firstName =
                    parts[0] && parts[0].toLowerCase() !== "null" ? parts[0] : "";
                  lastName =
                    parts[1] && parts[1].toLowerCase() !== "null" ? parts[1] : "";
                } else {
                  firstName =
                    items?.first_name && items?.first_name.toLowerCase() !== "null"
                      ? items.first_name
                      : "";
                  lastName =
                    items?.last_name && items?.last_name.toLowerCase() !== "null"
                      ? items.last_name
                      : "";
                }
                let isValidValue = position.some(
                  (opt) => opt._id === items?.designation
                );
                return (
                  <tr className="border-b border-slate-200 group" key={index}>
                    <td className="py-3 px-2 font-semibold text-sm text-left max-w-[240px] align-middle">
                      {firstName}
                    </td>
                    <td className="py-3 px-2 font-semibold text-sm text-left max-w-[240px] align-middle">
                      {lastName || ""}
                    </td>
                    <td className="py-3 px-2 font-semibold text-sm text-left max-w-[240px] align-middle">
                      {items?.email || "N/A"}
                    </td>
                    <td className="py-3 px-2 text-left max-w-[240px]">
                      <Select
                        xPlacement={"left"}
                        selectedValue={isValidValue ? items?.designation : ""}
                        dropdownClass={"!w-60"}
                        dropdownButtonLabelClass={"!font-bold"}
                        search={true}
                        dropdownData={[
                          { _id: "", label: t("select_position"), value: "" },
                          ...position.map((item) => ({
                            _id: item?._id,
                            label: item?.name,
                            value: item?._id,
                          })),
                        ]}
                        dropdownOptionBold={true}
                        getSelectedValue={(e) =>
                          handelUpdate(items._id, e._id, "designation", index)
                        }
                      />
                    </td>
                    <td className="py-3 px-2 font-semibold text-sm text-left max-w-[240px] align-middle">
                      {items && items?.created_at
                        ? moment(new Date(items.created_at)).format("ll")
                        : "N/A"}
                    </td>
                    <td className="py-3 px-2 font-semibold text-sm text-left max-w-[240px] align-middle">
                      {items?.role_details?.code !== "customer-admin" && (
                        <div className="flex items-start justify-start gap-2">
                          <div
                            className=""
                            data-tooltip-id={`buttonEdit${items?._id}`}
                            data-tooltip-place="right"
                          >
                            <Button
                              buttonClasses={
                                "!p-0 !flex !items-center !justify-center !text-base !bg-transparent !w-6 !h-6 !text-slate-400"
                              }
                              buttonType={"button"}
                              buttonIcon={"fa-solid fa-user-pen"}
                              buttonIconPosition={"left"}
                              buttonHasLink={false}
                              functions={() => {
                                setDetails(items);
                                setEnabledManageEmployeePopup(true);
                              }}
                            />
                            <Tooltip
                              id={`buttonEdit${items?._id}`}
                              className="!text-xs !bg-dark-teal-600 z-50 rounded py-3 px-6"
                              render={() => (
                                <div className="gap-x-1 gap-y-1">
                                  <div className="text-[13px] font-normal leading-none flex items-center">
                                    <div className="font-semibold">
                                      Click To Update Employee
                                    </div>
                                  </div>
                                </div>
                              )}
                            />
                          </div>
                          <div
                            className=""
                            data-tooltip-id={`buttons${items?._id}`}
                            data-tooltip-place="right"
                          >
                            <Button
                              buttonClasses={
                                "!p-0 !flex !items-center !justify-center !text-base !bg-transparent !w-6 !h-6 !text-rose-500"
                              }
                              buttonType={"button"}
                              buttonIcon={"fa-regular fa-trash-can"}
                              buttonIconPosition={"left"}
                              buttonHasLink={false}
                              functions={() => {
                                setCurrentId(items?._id);
                                setenabledDeletePopup(true);
                                setIsUpdate(false);
                              }}
                            />
                            <Tooltip
                              id={`buttons${items?._id}`}
                              className="!text-xs !bg-dark-teal-600 z-50 rounded py-3 px-6"
                              render={() => (
                                <div className="gap-x-1 gap-y-1">
                                  <div className="text-[13px] font-normal leading-none flex items-center">
                                    <div className="font-semibold">
                                      Click To Delete Employee
                                    </div>
                                  </div>
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ) : (
            <tbody className="text-slate-700 text-sm font-light bg-white">
              <tr className="border-b border-slate-200 group">
                <td
                  className="py-3 px-2 font-semibold text-sm text-left max-w-[240px] align-middle"
                  colspan={6}
                >
                  {loading ? (
                    <TableSkeleton tableImage={false} />
                  ) : (
                    <NoDataFound label={t("no_data_found")} />
                  )}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>
      {noOfPage > 1 ? (
        <Pagination
          handlePageClick={handlePageClick}
          noOfPage={noOfPage}
          startDataCount={startDataCount}
          endDataCount={endDataCount}
          count={totalDataCount}
          forcePage={currentPage}
        />
      ) : (
        <div className="flex justify-center sm:justify-between my-4 text-sm text-black text-center sm:text-left">
          {totalDataCount > 0 ? (
            <div>
              <p>
                {t("showing")}: {startDataCount} to {endDataCount} of{" "}
                {totalDataCount}
              </p>
            </div>
          ) : (
            ""
          )}
        </div>
      )}
      {enabledManageEmployeePopup && (
        <ManageEmployeePopup
          isOpen={enabledManageEmployeePopup}
          setIsOpen={(val) => setEnabledManageEmployeePopup(val)}
          getActionValue={(obj) => {
            setActionValue(obj);
          }}
          data={details}
          setDetails={setDetails}
        />
      )}
      {enabledDeletePopup && (
        <DeletePopup
          isOpen={enabledDeletePopup}
          setIsOpen={(val) => setenabledDeletePopup(val)}
          currentId={currentId}
          getActionValue={(obj) => {
            setActionValue(obj);
            setenabledDeletePopup(false);
            setCurrentId("");
          }}
        />
      )}
      <EmployeeUploadPopup
        isOpen={enabledUploadEmployeePopup}
        setIsOpen={(val) => setEnabledUploadEmployeePopup(val)}
        currentId={currentId}
        loadList={loadList}
        loadPositionList={loadPositionList}
        getActionValue={(obj) => {
          setActionValue(obj);
          setEnabledUploadEmployeePopup(false);          
          setCurrentId("");
        }}
      />
      {
        enabledExternalEmployeePopup && (
        <ExternalEmployeePopup
          isOpen={enabledExternalEmployeePopup}
          setIsOpen={(val) => setEnabledExternalEmployeePopup(val)}
          currentId={currentId}
          loadList={loadList}
          loadPositionList={loadPositionList}
          getActionValue={(obj) => {
            setActionValue(obj);
            setEnabledExternalEmployeePopup(false); 
          }}
        />
      )}
    </>
  );
};

export default CompanyEmployees;
